import { Messages } from '../../codegen/Localize';
import { DeprecatedEventWrapper } from '../../common/analyticsEvents/DeprecatedEventWrapper';
import { FeatureFlags } from '../FeatureFlags';
import { Helpers } from '../helpers/Helpers';
import { Module } from '../Module';
import { DeprecatedAnalyticsService } from './DeprecatedAnalyticsService';
import { ServerService } from './ServerService';

// TODO: De-Angularify HelpService

export class HelpService {
  // Needed for offline help, see wgapp/app/mixins/help_support.rb
  private static ONLINE_TOKEN_OFFLINE_PAGE_DICT: _.Dictionary<string> = {
    'adminusers2': 'csvguidelines.htm',
    'status1': 'service_viewstatus.htm',
    'settings-adsync1': 'groups_globalsync.htm'
  };
  private static tableauUrl = 'https://www.tableau.com';
  private static tableauKnowledgeBaseUrl = 'https://kb.tableau.com';

  private deprecatedEventFactory: DeprecatedEventWrapper;

  public static $inject = [
    '$window',
    'DeprecatedAnalyticsService',
    'ServerService'
  ];

  constructor(
    private $window: ng.IWindowService,
    private deprecatedAnalytics: DeprecatedAnalyticsService,
    private server: ServerService) {
    if (FeatureFlags.OnlineAnalyticsPipelineEnabled) {
      this.deprecatedEventFactory = this.deprecatedAnalytics.EventFactory(DeprecatedEventWrapper.eventTypes.InfoMenu_SearchHelp);
    }
  }

  public search(searchText: string): void {
    searchText = encodeURIComponent(searchText);

    if (FeatureFlags.OnlineAnalyticsPipelineEnabled){
      // Deprecated, use TelemetryService instead of EventFactory.
      this.deprecatedEventFactory.sendEvent(
        DeprecatedEventWrapper.eventTypes.InfoMenu_SearchHelp,
        'enterKeypress',
        {'searchText': searchText});
    }
    if (this.server.offlineHelpEnabled()) {
      // Example: http://localhost/offline_help/en_us/help.htm#search-workbooks
      this.$window.open(this.offlineHelpRoute('search-') + searchText, '_blank');
    } else {
      // Example: https://www.tableau.com/search/support/workbooks
      this.$window.open(this.supportUrlPrefix() + '/search/support/' + searchText, '_blank');
    }
  }

  public getServerHelpUrlByCategory(category?: string): string {
    if (this.server.offlineHelpEnabled()) {
      return this.offlineHelpRoute(HelpService.ONLINE_TOKEN_OFFLINE_PAGE_DICT[category]);
    } else {
      const lang = this.helpUrlLanguage();
      // Params must be ordered: edition, lang, platform, version

      const query = Helpers.serialize({
        'edition': this.server.helpEdition(),
        'lang': lang,
        'platform': this.serverPlatform(),
        'version': this.externalVersion()
      });
      // Example: https://www.tableau.com/en-us/app/server-views1?edition=server&lang=en-us&platform=windows&version=9.0
      return HelpService.tableauUrl + '/' + lang + '/app/' + 'server-' + (category ?  category : 'help1') + '?' + query;
    }
  }

  public getHelpUrlByCategory(category?: string, edition?: string, version?: string): string {
    const lang = this.helpUrlLanguage();

    // Params must be ordered: edition, lang, platform, version
    const query = Helpers.serialize({
      'edition': edition ? edition : this.server.helpEdition(),
      'lang': lang,
      'platform': this.serverPlatform(),
      'version': version ? version : this.externalVersion()
    });
    // Example: https://www.tableau.com/en-us/app/server-views1?edition=server&lang=en-us&platform=windows&version=9.0
    return HelpService.tableauUrl + '/' + lang + '/app/' + (category ?  category : 'server-help1') + '?' + query;
  }

  public getGeneralSupportUrl(): string {
    if (this.server.helpEdition() !== 'online') {
      return this.supportUrlPrefix() + '/support';
    }
    // Open a support request (i.e., Zeus support form)
    return 'https://' + this.server.sessionSupportHost() + this.server.sessionSupportPath() +
        '?' + Helpers.serialize({
          'uid':              this.server.sessionAuthUserId(),
          'serverSiteUserId': this.server.sessionUserId(),
          'serverSiteId':     this.server.sessionSiteLuid(),
          'serverInstanceId': this.server.sessionServerId()
        });
  }

  public getWhatIsNewUrl(): string{
      return this.supportUrlPrefix() + '/support/releases/online';
  }

  public getDatasourceHelpUrl(): string {
    const datasourceHelpUrl = this.server.datasourceHelpUrl();
    if(!datasourceHelpUrl) {
      return '';
    }
    const queryConnector = (datasourceHelpUrl.indexOf('?') >= 0) ? '&' : '?';
    const query = Helpers.serialize({
      'lang': this.helpUrlLanguage(),
      'version': this.externalVersion()
    });
    return datasourceHelpUrl + queryConnector + query;
  }

  public getHelpLinkText(): string {
    return this.server.helpEdition() === 'online' ? Messages.onlineHelp() : Messages.serverHelp();
  }

  public getKbLinkText(): string {
    return Messages.knowledgeBase();
  }

  public getSignInFailedSiteUserNotFoundKbUrl() {
    const kbPageId = 'error-errorcode-4-this-user-account-is-not-active-for-help-contact-your-tableau-online-administrator-signing-into-tableau-online';
    return this.getKbArticleUrl(kbPageId);
  }

  public getQueryParams(lang?: string): string {
    return  Helpers.serialize({
      'edition': this.server.helpEdition(),
      'lang': lang || this.helpUrlLanguage(),
      'platform': this.serverPlatform(),
      'version': this.externalVersion()
    });
  }

  // Helpers

  private supportUrlPrefix(): string {
    const lang = this.helpUrlLanguage();
    return HelpService.tableauUrl + (lang === 'en-us' ? '' : '/' + lang);
  }

  private offlineHelpRoute(page?: string): string {
    const lang = this.helpUrlLanguage().replace('-', '_');
    return 'offline_help/' + lang + '/help.htm' + (page ? '#' + page : '');
  }

  public externalVersion(): string {
    const server = this.server ? this.server.sessionServerVersion() : '';
    if (!server) {
      return '0.0';
    }
    const version = server.externalVersion;
    const intRegExp = /^\d+$/;
    if (intRegExp.test(version.major) && intRegExp.test(version.minor)) {
      return version.major + '.' + version.minor;
    } else {
      return '0.0';
    }
  }

  public helpUrlLanguage(): string {
    const language = this.server.sessionLanguage();
    switch(language) {
      case 'de_DE':
      case 'en_GB':
      case 'en_US':
      case 'es_ES':
      case 'fr_FR':
      case 'ja_JP':
      case 'ko_KR':
      case 'pt_BR':
      case 'zh_CN':
      case 'zh_TW':
        return language.toLowerCase().replace('_', '-');

      case 'de': return 'de-de';
      case 'en': return 'en-us';
      case 'es': return 'es-es';
      case 'fr': return 'fr-fr';
      case 'ja': return 'ja-jp';
      case 'ko': return 'ko-kr';
      case 'pt': return 'pt-br';
      case 'zh': return 'zh-cn';
      default: return 'en-us';
    }
  }

  private serverPlatform(): string {
    const server = this.server.sessionServerVersion();
    const platform = server ? server.platform : '';
    return (platform) ? platform.toLowerCase() : 'windows'; // Default to Windows
  }

  private getKbArticleUrl(pageId: string): string {
    const lang = this.server.sessionLanguage();
    const langQuery = lang ? '?lang=' + lang : '';
    return HelpService.tableauKnowledgeBaseUrl + '/articles/Issue/' + pageId + langQuery;
  }

}

Module.instance().service('HelpService', HelpService);

/**
 * Add a filter to convert help tokens into urls using the help service, so that
 * templates can use tokens directly without having to inject the service and use it
 * in controllers
 */
Module.instance().filter('toServerHelpUrl', ['HelpService', function(helpService: HelpService) {
  return function(token: string) {
    return helpService.getServerHelpUrlByCategory(token);
  };
}]);
